import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "components/mui";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { format, parseISO } from "date-fns";
import { useNotificationProvider } from "hooks";
import {
  GetHardFailedContacts,
  CleanHardFailedContacts,
} from "api/canmail/CANmailAPI";

const formatFailDate = (isoString) => {
  if (!isoString) return "—";
  try {
    return format(parseISO(isoString), "PP");
  } catch {
    return "—";
  }
};

const CleanListModal = ({ open, onClose, listId, listName, onCleaned }) => {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);

  const loadPreview = useCallback(async () => {
    if (!listId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await GetHardFailedContacts(listId);
      setContacts(Array.isArray(data?.contacts) ? data.contacts : []);
    } catch (err) {
      console.error("[CleanListModal] Error loading hard-bounced contacts:", err);
      setError("Failed to load the list of hard-bounced emails. Please try again.");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [listId]);

  // Load the preview whenever the modal opens for a list.
  useEffect(() => {
    if (open && listId) {
      loadPreview();
    }
    if (!open) {
      // Reset when closed so the next open starts fresh.
      setContacts([]);
      setError(null);
      setLoading(false);
      setCleaning(false);
    }
  }, [open, listId, loadPreview]);

  const handleClean = async () => {
    if (!listId) return;
    setCleaning(true);
    try {
      const data = await CleanHardFailedContacts(listId);
      const removedCount = data?.removed_count ?? 0;

      if (removedCount > 0) {
        notifySuccess(
          `Removed ${removedCount} hard-bounced ${removedCount === 1 ? "email" : "emails"} from "${listName}".`
        );
      } else {
        notifySuccess("No hard-bounced emails needed removing.");
      }

      onCleaned?.(data?.removed ?? []);
      onClose?.();
    } catch (err) {
      console.error("[CleanListModal] Error cleaning list:", err);
      notifyError("Failed to clean the list. Please try again.");
    } finally {
      setCleaning(false);
    }
  };

  const hasContacts = contacts.length > 0;

  return (
    <Dialog open={open} onClose={cleaning ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CleaningServicesIcon color="warning" />
        Clean Mailing List
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Cleaning removes contacts from <strong>{listName || "this list"}</strong> whose
            email address has <strong>hard-bounced</strong> (permanently failed to deliver) in
            your email history. This only removes their membership from this list — the contacts
            themselves are not deleted. A consent-log entry is recorded for each removal.
          </Typography>
        </Alert>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              minHeight: 220,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Checking for hard-bounced emails…
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : !hasContacts ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              No hard-bounced emails found on this list.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There is nothing to clean.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              {contacts.length} {contacts.length === 1 ? "email" : "emails"} will be removed
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Last failed send</TableCell>
                    <TableCell align="right">Hard fails</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => {
                    const name = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
                    return (
                      <TableRow key={contact.contactId} hover>
                        <TableCell>{name || "Unnamed contact"}</TableCell>
                        <TableCell>
                          <Chip label={contact.email} size="small" variant="outlined" color="error" />
                        </TableCell>
                        <TableCell>{formatFailDate(contact.lastFailDate)}</TableCell>
                        <TableCell align="right">{contact.failCount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={cleaning}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleClean}
          disabled={loading || cleaning || !hasContacts}
          startIcon={cleaning ? <CircularProgress size={16} color="inherit" /> : <CleaningServicesIcon />}
        >
          {cleaning
            ? "Removing…"
            : hasContacts
            ? `Remove ${contacts.length} ${contacts.length === 1 ? "email" : "emails"}`
            : "Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CleanListModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  listId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  listName: PropTypes.string,
  onCleaned: PropTypes.func,
};

export default CleanListModal;
